import { useState, useCallback } from 'react';
import { calculatePace } from '../utils';

/**
 * Parameters for updating lap data
 */
interface UpdateLapDataParams {
  /** Distance of new segment in meters */
  distanceMeters?: number;
  /** Time of new segment in milliseconds */
  segmentTime?: number;
  /** Pace of new segment */
  segmentPace?: number | null;
  /** Unit for pace calculation ('mile' or 'km') */
  paceUnit?: 'mile' | 'km';
  /** Whether to force lap pace recalculation */
  forceRecalculateLapPace?: boolean;
  /** Optional new lap pace value for forced updates */
  newLapPace?: number | null;
}

/**
 * Return type for useLapSegmentData hook
 */
interface LapSegmentDataResult {
  /** Current lap distance in meters */
  currentLapDistance: number;
  /** Current lap time in milliseconds */
  currentLapTime: number;
  /** Current lap pace in minutes per unit */
  currentLapPace: number | null;
  /** Last segment pace in minutes per unit */
  lastSegmentPace: number | null;
  /** Pace of the last lap that was completed */
  lastCompletedLapPace: number | null;
  /** Update lap data when a new segment is recorded */
  updateLapData: (params: UpdateLapDataParams) => number | null;
  /** Reset lap-specific segment data */
  resetLapSegmentData: (preserveLastSegmentPace?: boolean) => void;
  /** Reset lap data but preserve the last segment pace */
  resetLapDataPreservingPace: (completedLapPace?: number | null) => void;
  /** Set the last segment pace directly */
  setLastSegmentPace: React.Dispatch<React.SetStateAction<number | null>>;
  /** Set the last completed lap pace directly */
  setLastCompletedLapPace: React.Dispatch<React.SetStateAction<number | null>>;
}

/**
 * Custom hook for managing lap-specific segment data
 * Handles tracking distance, time, and pace for the current lap
 *
 * @returns Lap segment data state and functions
 */
const useLapSegmentData = (): LapSegmentDataResult => {
  // State for current lap metrics
  const [currentLapDistance, setCurrentLapDistance] = useState<number>(0);
  const [currentLapTime, setCurrentLapTime] = useState<number>(0);
  const [currentLapPace, setCurrentLapPace] = useState<number | null>(null);
  const [lastSegmentPace, setLastSegmentPace] = useState<number | null>(null);
  const [lastCompletedLapPace, setLastCompletedLapPace] = useState<number | null>(null);

  /**
   * Update lap data when a new segment is recorded
   * @param params - Parameters for lap data update
   * @returns The newly calculated currentLapPace
   */
  const updateLapData = useCallback(
    ({
      distanceMeters = 0,
      segmentTime = 0,
      segmentPace = null,
      paceUnit = 'mile',
      forceRecalculateLapPace = false,
      newLapPace = null,
    }: UpdateLapDataParams): number | null => {
      // Validate inputs
      const validDistance = typeof distanceMeters === 'number' && isFinite(distanceMeters);
      const validTime = typeof segmentTime === 'number' && isFinite(segmentTime);
      const validPace =
        segmentPace === null || (typeof segmentPace === 'number' && isFinite(segmentPace));

      if (!validDistance || !validTime || !validPace) {
        console.warn('Invalid inputs to updateLapData:', {
          distanceMeters,
          segmentTime,
          segmentPace,
        });
        return null;
      }

      // Special case for unit changes - update pace without changing distance/time
      if (forceRecalculateLapPace && newLapPace !== null && isFinite(newLapPace)) {
        setCurrentLapPace(newLapPace);
        return newLapPace;
      }

      // Normal case - update for new segment
      let calculatedNewPace: number | null = null;

      // Calculate new totals *before* setting state
      const newLapDistance = currentLapDistance + (distanceMeters > 0 ? distanceMeters : 0);
      const newLapTime = currentLapTime + (segmentTime > 0 ? segmentTime : 0);

      // Update state with new totals
      if (distanceMeters > 0) {
        setCurrentLapDistance(newLapDistance);
      }
      if (segmentTime > 0) {
        setCurrentLapTime(newLapTime);
      }

      // Calculate and update lap pace using the *new* totals calculated above
      if ((distanceMeters > 0 || segmentTime > 0) && newLapDistance > 0) {
        calculatedNewPace = calculatePace(newLapTime, newLapDistance, paceUnit);

        if (calculatedNewPace !== null && isFinite(calculatedNewPace)) {
          setCurrentLapPace(calculatedNewPace);
        } else {
          calculatedNewPace = null;
        }
      } else {
        calculatedNewPace = currentLapPace;
      }

      // Update last segment pace only if a valid pace is provided
      if (segmentPace !== null && isFinite(segmentPace)) {
        setLastSegmentPace(segmentPace);
      }

      return calculatedNewPace;
    },
    [currentLapDistance, currentLapTime, currentLapPace]
  );

  /**
   * Reset lap-specific segment data
   * Called when starting a new lap or resetting the timer
   * @param preserveLastSegmentPace - Whether to preserve the last segment pace
   */
  const resetLapSegmentData = useCallback((preserveLastSegmentPace = false) => {
    setCurrentLapDistance(0);
    setCurrentLapTime(0);
    setCurrentLapPace(null);
    setLastCompletedLapPace(null);

    // Only reset the last segment pace if not preserving it
    if (!preserveLastSegmentPace) {
      setLastSegmentPace(null);
    }
  }, []);

  /**
   * Reset lap data but preserve the last segment pace
   * Used specifically when auto-lapping with distance buttons
   * @param completedLapPace - Optional pace of the lap that just completed
   */
  const resetLapDataPreservingPace = useCallback(
    (completedLapPace?: number | null) => {
      // Capture the last segment pace before resetting
      const preservedLastSegmentPace = lastSegmentPace;

      // Important: Set the last completed lap pace first if a valid value was passed
      // This ensures it's set before any other state changes
      if (
        completedLapPace !== undefined &&
        completedLapPace !== null &&
        isFinite(completedLapPace)
      ) {
        // Update lastCompletedLapPace with the provided value (which should be the combined
        // pace of all segments in the completed lap)
        setLastCompletedLapPace(completedLapPace);
      }

      // Reset the lap data (distance and time) and current pace
      setCurrentLapDistance(0);
      setCurrentLapTime(0);
      setCurrentLapPace(null); // Reset current lap pace for the new lap

      // Restore the last segment pace
      if (preservedLastSegmentPace !== null && isFinite(preservedLastSegmentPace)) {
        setLastSegmentPace(preservedLastSegmentPace);
      }
    },
    [lastSegmentPace]
  );

  return {
    // State
    currentLapDistance,
    currentLapTime,
    currentLapPace,
    lastSegmentPace,
    lastCompletedLapPace,

    // Functions
    updateLapData,
    resetLapSegmentData,
    resetLapDataPreservingPace,
    setLastSegmentPace,
    setLastCompletedLapPace,
  };
};

export default useLapSegmentData;
